import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, User, HelpCircle } from 'lucide-react';
import { supabase } from '../supabase/client';
import { categoryStore } from '../store/categoryStore';
import LogoWithFallback from '../components/LogoWithFallback';

interface Level {
  id: string;
  name: string;
}

interface SubCategory {
  id: string;
  name: string;
  code: string;
  icon: string;
  color: string;
  description: string;
  levels: Level[];
}

interface CategoryConfig {
  id: string;
  name: string;
  color: string;
  subCategories: SubCategory[];
}

export default function CategoryPage() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [categoryConfig, setCategoryConfig] = useState<CategoryConfig | null>(null);
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      // 确保 categoryStore 已初始化
      await categoryStore.init();

      // 从 categoryStore 获取数据（来源于【类目与定价】栏目，实时同步）
      const organizations = categoryStore.getOrganizations();
      const categories = categoryStore.getCategories();
      const levels = categoryStore.getLevels();

      // 根据 categoryId 找到对应的考评体系
      // categoryId 可能是 organization 的 id
      const org = organizations.find(o => o.id === categoryId);
      if (org) {
        // 获取该考评体系下的课程分类
        const orgCategories = categoryStore.getCategoriesByOrg(org.id);
        const subCategories: SubCategory[] = orgCategories.map(cat => {
          // 获取该课程分类下的级别
          const catLevels = categoryStore.getLevelsByCategory(cat.id);
          return {
            id: cat.id,
            name: cat.name,
            code: cat.code,
            icon: 'Palette',
            color: '#3b82f6',
            description: cat.name,
            levels: catLevels.map(l => ({ id: l.id, name: l.name }))
          };
        });

        const config: CategoryConfig = {
          id: org.id,
          name: org.name,
          color: '#3b82f6',
          subCategories
        };
        setCategoryConfig(config);
      } else {
        // 如果找不到对应的考评体系，尝试用 categoryId 作为 course_category 的 id
        const cat = categories.find(c => c.id === categoryId);
        if (cat) {
          const parentOrg = organizations.find(o => o.id === cat.orgId);
          const catLevels = categoryStore.getLevelsByCategory(cat.id);
          const config: CategoryConfig = {
            id: cat.id,
            name: parentOrg ? `${parentOrg.name} - ${cat.name}` : cat.name,
            color: '#3b82f6',
            subCategories: [{
              id: cat.id,
              name: cat.name,
              code: cat.code,
              icon: 'Palette',
              color: '#3b82f6',
              description: cat.name,
              levels: catLevels.map(l => ({ id: l.id, name: l.name }))
            }]
          };
          setCategoryConfig(config);
        } else {
          setCategoryConfig(null);
        }
      }

      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (authUser) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', authUser.id)
          .single();
        setUser(profile);
      }
      setLoading(false);
    };
    loadData();
  }, [categoryId]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    navigate('/');
  };

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">加载中...</div>
      </div>
    );
  }

  if (!categoryConfig) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">未找到对应的分类信息</p>
          <Link to="/" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">返回首页</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 hover:opacity-90 transition-opacity">
              <LogoWithFallback className="h-10 w-10 object-contain" />
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/help" className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                <HelpCircle className="w-4 h-4" />常见问题
              </Link>
              <Link to="/profile" className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                <User className="w-4 h-4" />学员中心
              </Link>
              <div className="h-6 w-px bg-gray-200 mx-1" />
              {user ? (
                <>
                  <span className="text-sm text-gray-700">{user.phone}</span>
                  <button onClick={handleLogout} className="px-4 py-2 text-sm text-red-600 hover:text-red-700 transition-colors">退出</button>
                </>
              ) : (
                <>
                  <Link to="/login" className="px-4 py-2 text-sm text-gray-600 hover:text-blue-600 transition-colors">登录</Link>
                  <Link to="/register" className="px-5 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">注册</Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{categoryConfig.name}</h1>
          <p className="text-gray-500">选择考试科目和等级</p>
        </motion.div>

        <div className="grid gap-4">
          {categoryConfig.subCategories.map((sub, index) => (
            <motion.div
              key={sub.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl border border-gray-100 overflow-hidden"
            >
              <button
                onClick={() => toggleExpand(sub.id)}
                className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: sub.color + '20' }}>
                    <span className="text-2xl font-bold" style={{ color: sub.color }}>{sub.name[0]}</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">{sub.name}</h3>
                    <p className="text-sm text-gray-500">{sub.description}</p>
                  </div>
                </div>
                <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${expandedId === sub.id ? 'rotate-90' : ''}`} />
              </button>
              <AnimatePresence>
                {expandedId === sub.id && (
                  <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                    <div className="px-6 pb-6 pt-2">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {sub.levels.map((level) => (
                          <Link
                            key={level.id}
                            to={`/category/${categoryId}/${sub.id}/${level.id}`}
                            className="p-4 bg-gray-50 rounded-lg hover:bg-blue-50 hover:border-blue-200 border border-transparent transition-all text-center"
                          >
                            <span className="font-medium text-gray-700">{level.name}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}